import { useQuery } from '@tanstack/react-query'

import { departmentsApi } from '@/features/masters/departments/api'
import { machinesApi } from '@/features/masters/machines/api'
import { failureReasonsApi } from '@/features/maintenance/api'
import { usePlantId } from '@/shared/hooks/usePlantId'

/**
 * Common name lookups every maintenance report needs: machine, department,
 * failure reason. Cached for 5 minutes so a heavy report screen with many
 * drilldown drawers doesn't hammer the API.
 */
export function useMasterLookups() {
  const { plantId } = usePlantId()

  const { data: machinesData } = useQuery({
    queryKey: ['machines-for-reports', plantId],
    enabled: !!plantId,
    staleTime: 5 * 60_000,
    queryFn: () => machinesApi.list({ plant_id: plantId, is_active: true, page: 1, page_size: 500 }),
  })
  const machines = machinesData?.items ?? []

  const { data: deptsData } = useQuery({
    queryKey: ['departments-all'],
    staleTime: 5 * 60_000,
    queryFn: () => departmentsApi.list({ page: 1, page_size: 200 }),
  })
  const depts = deptsData?.items ?? []

  const { data: failureReasons } = useQuery({
    queryKey: ['failure-reasons'],
    staleTime: 5 * 60_000,
    queryFn: () => failureReasonsApi.list(),
  })

  return {
    machines,
    depts,
    failureReasons: failureReasons ?? [],
    machineLabel: (id: string | null | undefined) => {
      if (!id) return '—'
      const m = machines.find((x) => x.id === id)
      return m ? `${m.machine_id_str} — ${m.name}` : id.slice(0, 8)
    },
    machineCode: (id: string | null | undefined) => {
      if (!id) return '—'
      return machines.find((x) => x.id === id)?.machine_id_str ?? id.slice(0, 8)
    },
    deptName: (id: string | null | undefined) => {
      if (!id) return '—'
      return depts.find((x) => x.id === id)?.name ?? id.slice(0, 8)
    },
    failureReasonName: (id: string | null | undefined) => {
      if (!id) return '—'
      return failureReasons?.find((r) => r.id === id)?.reason_name ?? id.slice(0, 8)
    },
  }
}
